import React from "react";
import { Plane } from "lucide-react";

const TIPS = [
  "Your booking comes with 24/7 support…",
  "Tip: filter by Direct only to skip layovers.",
  "Did you know? Branded fares unlock extra bags & meals.",
  "We compare GDS, NDC and low-cost feeds in parallel.",
  "Prices update in real time — securing your fare…",
];

/**
 * Centered modal loader for flight search.
 * Overlays the entire viewport while the inventory query is in-flight.
 *
 * Accepts an array of {from, to, date} segments so round-trip and multi-city
 * searches show every leg (not just first.from → last.to which would render
 * "CAI → CAI" for a round-trip).
 */
export default function FlightLoaderModal({ segments, from, to, date, pax = 1 }) {
  // Back-compat: if a caller still passes the legacy {from, to, date} props,
  // wrap them into a single-segment array.
  const legs = (segments && segments.length)
    ? segments
    : (from || to ? [{ from, to, date }] : []);
  const [tip, setTip] = React.useState(0);
  React.useEffect(() => {
    const t = setInterval(() => setTip((i) => (i + 1) % TIPS.length), 1800);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="flight-loader-modal" className="fixed inset-0 z-[60] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in duration-200" role="dialog" aria-modal="true" aria-busy="true">
      <style>{`
        @keyframes lm-bar { 0% { transform: translateX(-100%); } 100% { transform: translateX(220%); } }
        @keyframes lm-plane { 0%,100% { transform: translateX(0) translateY(0) rotate(-6deg); } 50% { transform: translateX(8px) translateY(-3px) rotate(-2deg); } }
        @keyframes lm-dot { 0%,80%,100% { opacity: .25; transform: scale(.85); } 40% { opacity: 1; transform: scale(1); } }
        .lm-bar     { animation: lm-bar 1.6s cubic-bezier(.55,.0,.45,1) infinite; }
        .lm-plane   { animation: lm-plane 2.2s ease-in-out infinite; }
        .lm-dot-1   { animation: lm-dot 1.1s ease-in-out infinite; }
        .lm-dot-2   { animation: lm-dot 1.1s ease-in-out .15s infinite; }
        .lm-dot-3   { animation: lm-dot 1.1s ease-in-out .3s infinite; }
      `}</style>
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 max-w-md w-full p-7 sm:p-9 relative">
        {/* Route — one row per leg so round-trip and multi-city show every segment */}
        <div className="text-center space-y-2">
          {legs.length === 0 && (
            <div className="font-heading text-2xl font-bold text-[var(--brand-accent)]">Searching flights…</div>
          )}
          {legs.map((seg, i) => (
            <div key={i} data-testid={`flight-loader-leg-${i}`} className="font-heading text-xl sm:text-2xl font-bold text-[var(--brand-accent)] tracking-tight flex items-center justify-center gap-3 flex-wrap">
              {legs.length > 1 && (
                <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--brand-primary)] font-bold bg-teal-50 px-2 py-0.5 rounded-full">
                  Leg {i + 1}
                </span>
              )}
              <span className="font-mono">{seg.from || "—"}</span>
              <span className={(i === 0 ? "lm-plane " : "") + "inline-block text-[var(--brand-primary)]"}><Plane className="w-5 h-5 inline -mt-1" /></span>
              <span className="font-mono">{seg.to || "—"}</span>
              {seg.date && <span className="text-xs font-sans font-normal text-slate-500 ml-1">{seg.date}</span>}
            </div>
          ))}
          <div className="text-xs text-slate-500 inline-flex items-center gap-2 pt-1">
            <span>{pax} {pax === 1 ? "person" : "people"}</span>
          </div>
        </div>

        {/* Animated dots */}
        <div className="flex items-center justify-center gap-2 mt-7">
          <span className="lm-dot-1 inline-block w-2 h-2 rounded-full bg-[var(--brand-primary)]" />
          <span className="lm-dot-2 inline-block w-2 h-2 rounded-full bg-amber-500" />
          <span className="lm-dot-3 inline-block w-2 h-2 rounded-full bg-blue-500" />
        </div>

        {/* Rotating tip */}
        <div className="mt-6 text-center text-sm text-slate-600 min-h-[20px]" key={tip}>
          {TIPS[tip]}
        </div>

        {/* Indeterminate progress */}
        <div className="mt-6 h-1.5 w-full overflow-hidden bg-slate-100 rounded-full relative">
          <div className="lm-bar absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-[var(--brand-primary)] via-blue-500 to-[var(--brand-primary)] rounded-full" />
        </div>
      </div>
    </div>
  );
}
